/**
 * Supply sites on the map: depots, forward sites, and what is in transit.
 *
 * The parts table answers "how many"; the map answers "how far". A shortage at
 * a depot 400 km behind the line and the same shortage at the forward site the
 * aircraft launch from are different problems, and a list sorted by part number
 * cannot show that. This layer puts the stock where it physically is.
 *
 * Kinds are drawn differently because they mean different things. A depot is a
 * filled square — it holds, it does not move. A forward site is a ring, because
 * that is where the aircraft are and where a shortfall bites first. In-transit
 * is dashed and dimmer: it is a promise, not a shelf, and must never be read as
 * stock on hand — the same reason the sustainment endpoint leaves inbound out
 * unless it is asked for.
 *
 * Hovering reads out what is stocked there, and marks any count nobody has
 * verified. An unverified number displayed as a plain figure is how a plan gets
 * built on a shelf somebody assumed.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { bindHoverLayer } from '@/map/hoverLayer';
import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { subscribeToSupplyLocations } from '@/stores/supplyStore';
import type { Part, StockItem, SupplyLocation } from '@/api/supply';

export const SUPPLY_SOURCE_ID = 'supply-sites';
/** Exported: the hover affordance hit-tests this layer, and so do the tests. */
export const SUPPLY_HALO_LAYER = 'supply-sites-halo';
const SUPPLY_DEPOT_LAYER = 'supply-sites-depot';
const SUPPLY_LABEL_LAYER = 'supply-sites-label';

const EMPTY = { type: 'FeatureCollection', features: [] as unknown[] };

const KIND_COLOR: Record<string, string> = {
  depot: '#7fb6ff',
  forward: '#f0b53f',
  'in-transit': 'rgba(244,244,245,0.45)',
};

const KIND_LABEL: Record<string, string> = {
  depot: 'DEPOT',
  forward: 'FORWARD',
  'in-transit': 'IN TRANSIT',
};

function kindColor(kind: string): string {
  return KIND_COLOR[kind] ?? 'rgba(244,244,245,0.55)';
}

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind.toUpperCase();
}

/** How many stock lines a hover label lists before it summarizes the rest. */
const HOVER_LINE_CAP = 6;

/**
 * What is on the shelf at one site, biggest first, with unverified counts
 * called out. Exported so the label text can be tested without a map.
 */
export function stockLinesFor(
  locationId: string,
  stock: StockItem[],
  parts: Part[],
): string[] {
  const nameOf = new Map(parts.map((part) => [part.partId, part.name]));
  const here = stock
    .filter((item) => item.locationId === locationId && item.quantity > 0)
    .sort((a, b) => b.quantity - a.quantity);

  if (here.length === 0) return ['Nothing on the books here.'];

  const lines = here.slice(0, HOVER_LINE_CAP).map((item) => {
    const name = nameOf.get(item.partId) ?? item.partId;
    // The caveat travels with the number. A count nobody walked is not a count.
    const caveat = item.verifiedAt === null ? ' · UNVERIFIED' : '';
    return `${item.quantity} × ${name}${caveat}`;
  });

  if (here.length > HOVER_LINE_CAP) {
    lines.push(`+${here.length - HOVER_LINE_CAP} more line items`);
  }
  return lines;
}

export function supplyLocationFeature(
  location: SupplyLocation,
  stock: StockItem[],
  parts: Part[],
): unknown {
  return {
    type: 'Feature',
    properties: {
      locationId: location.locationId,
      name: location.name,
      kind: location.kind,
      color: kindColor(location.kind),
      label: `${kindLabel(location.kind)} · ${location.name}`,
      lines: JSON.stringify(stockLinesFor(location.locationId, stock, parts)),
    },
    geometry: { type: 'Point', coordinates: [location.lon, location.lat] },
  };
}

export interface SupplySiteHover {
  /** Map-container pixel coordinates of the pointer. */
  x: number;
  y: number;
  title: string;
  lines: string[];
  color: string;
}

/** The properties MapLibre hands back on a hovered feature. */
interface HoverEvent {
  features?: Array<{ properties: Record<string, unknown> | null } | undefined>;
  point: { x: number; y: number };
}

export function hoverFromProperties(
  properties: Record<string, unknown>,
  point: { x: number; y: number },
): SupplySiteHover | null {
  const label = properties.label;
  if (typeof label !== 'string') return null;

  // MapLibre flattens feature properties to primitives, so the line array
  // travels as JSON. A malformed value must not take the map down with it.
  let lines: string[] = [];
  if (typeof properties.lines === 'string') {
    try {
      const parsed: unknown = JSON.parse(properties.lines);
      if (Array.isArray(parsed)) lines = parsed.filter((l): l is string => typeof l === 'string');
    } catch {
      lines = [];
    }
  }

  return {
    x: point.x,
    y: point.y,
    title: label,
    lines,
    color: typeof properties.color === 'string' ? properties.color : 'rgba(244,244,245,0.55)',
  };
}

export interface SupplyLayerOptions {
  onHover?: (hover: SupplySiteHover | null) => void;
}

export interface SupplyLayer {
  destroy: () => void;
  stats: () => { redraws: number; sites: number };
}

export function createSupplyLayer(map: MapLike, options: SupplyLayerOptions = {}): SupplyLayer {
  let redraws = 0;
  let drawn = 0;

  if (!map.getSource(SUPPLY_SOURCE_ID)) {
    map.addSource(SUPPLY_SOURCE_ID, { type: 'geojson', data: EMPTY });
  }

  if (!map.getLayer(SUPPLY_HALO_LAYER)) {
    map.addLayer({
      id: SUPPLY_HALO_LAYER,
      type: 'circle',
      source: SUPPLY_SOURCE_ID,
      paint: {
        'circle-radius': 9,
        'circle-color': 'rgba(0,0,0,0.3)',
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 2,
        // In-transit is dimmer on purpose: a delivery is a promise, and a
        // promise drawn as brightly as a shelf is how it gets counted as one.
        'circle-opacity': ['case', ['==', ['get', 'kind'], 'in-transit'], 0.35, 0.8],
      },
    });
  }

  // A depot gets a solid core; a forward site stays hollow, so the two are
  // distinguishable at a glance and in monochrome print.
  if (!map.getLayer(SUPPLY_DEPOT_LAYER)) {
    map.addLayer({
      id: SUPPLY_DEPOT_LAYER,
      type: 'circle',
      source: SUPPLY_SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'depot'],
      paint: { 'circle-radius': 3.5, 'circle-color': ['get', 'color'] },
    });
  }

  if (!map.getLayer(SUPPLY_LABEL_LAYER)) {
    map.addLayer({
      id: SUPPLY_LABEL_LAYER,
      type: 'symbol',
      source: SUPPLY_SOURCE_ID,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 10,
        'text-letter-spacing': 0.08,
        // Above the marker, where a command target's verb is below its own, so
        // a site sharing a coordinate with one does not smear into it.
        'text-offset': [0, -1.5],
      },
      paint: {
        'text-color': ['get', 'color'],
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  const unsubscribe = subscribeToSupplyLocations(({ locations, stock, parts }) => {
    const source = geoJsonSource(map, SUPPLY_SOURCE_ID);
    if (!source) return;
    source.setData({
      type: 'FeatureCollection',
      features: locations.map((location) => supplyLocationFeature(location, stock, parts)),
    });
    drawn = locations.length;
    redraws += 1;
  });

  const onHoverMove = (event: HoverEvent): void => {
    const properties = event.features?.[0]?.properties;
    if (!properties) return;
    options.onHover?.(hoverFromProperties(properties, event.point));
  };
  const onHoverLeave = (): void => options.onHover?.(null);

  const unbindHover = bindHoverLayer(map, SUPPLY_HALO_LAYER, onHoverMove, onHoverLeave);

  return {
    destroy: () => {
      unsubscribe();
      unbindHover();
      // A label left behind would hang over a map that no longer has the layer.
      options.onHover?.(null);
      for (const id of [SUPPLY_LABEL_LAYER, SUPPLY_DEPOT_LAYER, SUPPLY_HALO_LAYER]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(SUPPLY_SOURCE_ID)) map.removeSource(SUPPLY_SOURCE_ID);
    },
    stats: () => ({ redraws, sites: drawn }),
  };
}

/* The hovered site, published outside React — see useCommandTargetLayer for why
 * the layer is mounted by the screen and the label subscribes separately. */
const hoverListeners = new Set<(hover: SupplySiteHover | null) => void>();

function publishHover(hover: SupplySiteHover | null): void {
  for (const listener of hoverListeners) listener(hover);
}

/** Mount the layer. Call it from the screen, alongside the other layer hooks. */
export function useSupplyLayer(): void {
  const controller = useMapController();

  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createSupplyLayer(map, {
          onHover: (hover) => {
            const raw = controller.raw();
            if (raw) raw.getCanvas().style.cursor = hover ? 'pointer' : '';
            publishHover(hover);
          },
        });
        return () => layer.destroy();
      }),
    [controller],
  );
}

/** Roughly the label's width, for keeping it inside the viewport. */
const LABEL_WIDTH = 230;

/** The hover label for whatever supply site the pointer is over. */
export function SupplySiteHoverLabel(): ReactNode {
  const [hover, setHover] = useState<SupplySiteHover | null>(null);

  useEffect(() => {
    hoverListeners.add(setHover);
    return () => {
      hoverListeners.delete(setHover);
    };
  }, []);

  if (!hover) return null;

  const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const left = Math.min(hover.x + 14, viewportWidth - LABEL_WIDTH - 12);

  return (
    <div
      role="tooltip"
      className="glass pointer-events-none absolute z-40 rounded-[12px] px-3 py-2"
      style={{ left, top: hover.y + 14, maxWidth: LABEL_WIDTH }}
    >
      <span
        className="block font-mono text-[11px] font-semibold tracking-wide"
        style={{ color: hover.color }}
      >
        {hover.title}
      </span>
      {hover.lines.map((line) => (
        <span key={line} className="mt-0.5 block font-mono text-[9.5px] text-white/55">
          {line}
        </span>
      ))}
    </div>
  );
}

export default SupplySiteHoverLabel;
